import { Platform } from '@angular/cdk/platform';
import { AsyncPipe } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, NgZone, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { appendToRmaCdnUrl } from '@rma/common-ui-ng/util';
import { rmaShareReplay } from '@rma/common-ui-rxjs/operators';
import { ViewportHeightService } from '@rma/generic/util/device-size';
import { FooterComponent } from '@rma/site/private/orc-footer';
import { SiteHeaderComponent } from '@rma/site/private/orc-header';
import { catchError, filter, map, mergeMap, Observable, of } from 'rxjs';
import { LazyLoadThirdPartyScriptsService } from '../util-lazy-load-scripts/lazy-load-scripts';
@Component({
  selector: 'rma-root',
  templateUrl: './rma-layout.component.html',
  styleUrls: ['./rma-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LazyLoadThirdPartyScriptsService],
  imports: [AsyncPipe, FooterComponent, MatProgressSpinnerModule, RouterModule, SiteHeaderComponent],
})
export class RmaLayoutComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly viewportHeightService = inject(ViewportHeightService);
  private readonly zone = inject(NgZone);
  private readonly platform = inject(Platform);
  private readonly destroyRef = inject(DestroyRef);

  private readonly cdnUrl = inject(appendToRmaCdnUrl);

  // angular scrollPositionRestoration can have issues in non blink browsers
  protected readonly scrollSmooth = this.platform.BLINK;

  protected isLoading = false;
  protected basicHeader$: Observable<boolean>;
  protected fullWidthHeader$: Observable<boolean>;
  protected showFooter$: Observable<boolean>;
  protected showHeader$: Observable<boolean>;
  protected showMarketingNav$: Observable<boolean>;
  protected renownedBranding$: Observable<boolean>;
  protected loginHeader$: Observable<boolean>;

  constructor() {
    const lazyLoad3rdPartyScripts = inject(LazyLoadThirdPartyScriptsService);
    afterNextRender(() => lazyLoad3rdPartyScripts.initialize());
  }

  ngOnInit(): void {
    console.log(`The cdn url is: ${this.cdnUrl('')}`);

    this.zone.runOutsideAngular(() => {
      this.viewportHeightService.initialise();
      const routeData$ = this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        catchError(() =>
          of({
            hideFooter: false,
            hideHeader: false,
            basicHeader: false,
            fullWidthHeader: false,
            showMarketingNav: false,
            renownedBranding: false,
            loginHeader: false,
          }),
        ),
        rmaShareReplay(),
        takeUntilDestroyed(this.destroyRef),
      );

      this.showFooter$ = routeData$.pipe(map(({ hideFooter }) => !hideFooter));
      this.showHeader$ = routeData$.pipe(map(({ hideHeader }) => !hideHeader));
      this.basicHeader$ = routeData$.pipe(map(({ basicHeader }) => !!basicHeader));
      this.fullWidthHeader$ = routeData$.pipe(map(({ fullWidthHeader }) => !!fullWidthHeader));
      this.showMarketingNav$ = routeData$.pipe(map(({ showMarketingNav }) => !!showMarketingNav));
      this.renownedBranding$ = routeData$.pipe(map(({ renownedBranding }) => !!renownedBranding));
      this.loginHeader$ = routeData$.pipe(map(({ loginHeader }) => !!loginHeader));

      this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => this.setLoading(e));
    });
  }

  private setLoading(event: Event): void {
    if (event instanceof NavigationStart) {
      this.isLoading = true;
    } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      // Set loading state to false in these events to hide the spinner in case a request fails
      this.isLoading = false;
    }
  }
}
